import { darken } from '@mui/material/styles';
import Triangle from '../symbol/Triangle';
import StarFilled from '../symbol/StarFilled';
import StarBorderd from '../symbol/StarBordered';
import Receipt from '../symbol/Receipt';
import describeArc from '../../../lib/describeArc';
import Revoked from '../symbol/Revoked';
import Phone from '../symbol/Phone';
import Circle from '../symbol/Circle';
import Cross from '../symbol/Cross';

const SMALL_MARKER_RADIUS = 18;
const LARGE_MARKER_RADIUS = 20;

function GenericServiceProvider({
  serviceProvider,
  index,
  theme,
  size = 'medium',
  location,
  gap = 0,
}) {
  const doubleDigit = (parseInt(index, 10) + 1 || 0) > 9;

  let radius = LARGE_MARKER_RADIUS;
  let strokeWidth = 4;
  let textX = doubleDigit ? 24 : 25;

  let textY = 27;
  let symbol = doubleDigit
    ? {
        translateX: 23,
        translateY: 33,
      }
    : {
        translateX: 25,
        translateY: 33,
      };

  if (size === 'small') {
    radius = SMALL_MARKER_RADIUS;
    strokeWidth = 3;
    textX = doubleDigit ? 19 : 22;
    textY = 24;
    symbol = doubleDigit
      ? {
          translateX: 17,
          translateY: 27,
        }
      : {
          translateX: 20,
          translateY: 27,
        };
  }

  const viewBoxSize = radius * 2 + strokeWidth * 2;

  const quadrantGap = gap;

  const arcs = [
    {
      startAngle: 0 + quadrantGap,
      endAngle: 90 - quadrantGap,
      value: location?.skills?.servicesIndustry || false,
    },
    {
      startAngle: 90 + quadrantGap,
      endAngle: 180 - quadrantGap,
      value: location?.skills?.servicesProductLine || false,
    },
    {
      startAngle: 180 + quadrantGap,
      endAngle: 270 - quadrantGap,
      value: location?.skills?.servicesBrand || false,
    },
    {
      startAngle: 270 + quadrantGap,
      endAngle: 0 - quadrantGap,
      value: location?.skills?.withinTravelDistance,
    },
  ];

  let symbolToAdd = null;
  let symbolColor = '#000000';

  // Icon Precedence
  /*
   - ACCEPTED or ASSIGNED
   - ACCEPTED_WITH_CONDITIONS
   - REVOKED or REJECTED
   - PENDING_ACCEPTANCE (Dispatch Sent)
   - SP Status is InActive for Dispatch
   - SP is Non Compliant
   - SP has manual dispatch
   - SP is compliant and active, but skills dont match
   - Everything good but no dispatch
  */

  // Accepted or Assigned will be handled by other component
  if (location?.dispatchAcceptanceStatus === 'ACCEPTED_WITH_CONDITIONS') {
    symbolColor = theme.palette.success.main;
    symbolToAdd = StarFilled(
      theme,
      symbol.translateX,
      symbol.translateY,
      symbolColor,
    );
  } else if (
    ['REVOKED', 'REJECTED'].includes(location?.dispatchAcceptanceStatus)
  ) {
    symbolColor = '#ff0000';
    symbolToAdd = Revoked(
      theme,
      symbol.translateX,
      symbol.translateY,
      symbolColor,
    );
  } else if (
    ['PENDING_ACCEPTANCE'].includes(location?.dispatchAcceptanceStatus)
  ) {
    symbolColor = theme.palette.success.main;
    symbolToAdd = StarBorderd(
      theme,
      symbol.translateX,
      symbol.translateY,
      symbolColor,
    );
  } else if (serviceProvider?.status !== 'ACTIVE') {
    symbolColor = '#8F5601';
    symbolToAdd = Triangle(
      theme,
      symbol.translateX,
      symbol.translateY,
      symbolColor,
    );
  } else if (serviceProvider?.compliant !== true) {
    symbolColor = theme.palette.warning.main;
    symbolToAdd = Receipt(
      theme,
      symbol.translateX,
      symbol.translateY,
      symbolColor,
    );
  } else if (serviceProvider?.dispatchMethod === 'MANUAL') {
    // eslint-disable-next-line prefer-destructuring
    symbolColor = theme.palette.grey[500];
    symbolToAdd = Phone(
      theme,
      symbol.translateX,
      symbol.translateY,
      symbolColor,
    );
  } else if (!Object.values(location?.skills)?.every(value => value === true)) {
    symbolColor = theme.palette.error.main;
    symbolToAdd = Cross(
      theme,
      symbol.translateX,
      symbol.translateY,
      symbolColor,
    );
  } else {
    symbolColor = theme.palette.primary.main;
    symbolToAdd = Circle(
      theme,
      symbol.translateX,
      symbol.translateY,
      symbolColor,
    );
  }

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', viewBoxSize);
  svg.setAttribute('height', viewBoxSize);
  svg.setAttribute('viewBox', `0 0 ${viewBoxSize} ${viewBoxSize}`);

  const outerCircle = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'circle',
  );

  outerCircle.setAttribute('id', 'mapMarkerSelectionArea');
  outerCircle.setAttribute('cx', viewBoxSize / 2);
  outerCircle.setAttribute('cy', viewBoxSize / 2);
  outerCircle.setAttribute('r', radius);
  outerCircle.setAttribute('stroke', darken(symbolColor, 0.1));
  outerCircle.setAttribute('stroke-width', strokeWidth + 3);
  outerCircle.setAttribute('fill', 'none');
  outerCircle.setAttribute('stroke-opacity', '0.9');
  outerCircle.setAttribute('display', 'none');

  svg.appendChild(outerCircle);

  const innerCircle = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'circle',
  );

  innerCircle.setAttribute('cx', viewBoxSize / 2);
  innerCircle.setAttribute('cy', viewBoxSize / 2);
  innerCircle.setAttribute('r', radius - strokeWidth);
  innerCircle.setAttribute('fill', 'white');
  innerCircle.setAttribute('stroke', '#dedede');
  innerCircle.setAttribute('stroke-width', strokeWidth);
  innerCircle.setAttribute('id', 'mapMarkerSelectionAreaInner');

  svg.appendChild(innerCircle);

  arcs.forEach(item => {
    if (item.value) {
      const d = describeArc(
        viewBoxSize / 2,
        viewBoxSize / 2,
        radius - strokeWidth,
        item.startAngle,
        item.endAngle,
      );
      const path = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'path',
      );
      path.setAttribute('d', d);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', symbolColor);
      path.setAttribute('stroke-width', strokeWidth);
      svg.appendChild(path);
    }
  });

  svg.appendChild(symbolToAdd);

  const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  text.setAttribute('fill', '#000000');
  text.setAttribute('font-weight', '500');
  text.setAttribute('x', textX);
  text.setAttribute('y', textY);
  text.setAttribute('font-size', '12px');

  const textNode = document.createTextNode(
    (parseInt(index, 10) + 1).toString(),
  );
  text.appendChild(textNode);

  svg.appendChild(text);
  return svg;
}

export default GenericServiceProvider;
